import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { productsAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';

const formatAuditSnapshotStatus = (auditSnapshot) => {
  const status = auditSnapshot?.status || auditSnapshot?.reason;

  switch (status) {
    case 'captured':
      return 'Captured changes for the latest audit run';
    case 'no-changes':
      return 'Checked successfully, but no tracked product changes were found';
    case 'already-captured':
      return 'This Nepal-date audit snapshot was already captured';
    default:
      return null;
  }
};

const formatAuditSnapshotCounts = (auditSnapshot) => {
  if (!auditSnapshot) return null;

  const changedRows = Number(auditSnapshot.changedRows || 0);
  const newRows = Number(auditSnapshot.newRows || 0);
  const deactivatedRows = Number(auditSnapshot.deactivatedRows || 0);
  const unchangedRows = Number(auditSnapshot.unchangedRows || 0);

  if (
    changedRows <= 0
    && newRows <= 0
    && deactivatedRows <= 0
    && unchangedRows <= 0
  ) {
    return null;
  }

  return `Changed ${changedRows}, new ${newRows}, deactivated ${deactivatedRows}, unchanged ${unchangedRows}`;
};

const ProductSyncControl = () => {
  const { hasFeatureAccess } = useAuthStore();
  const canManageProductSync = hasFeatureAccess('products.sync');

  const [syncStatusLoading, setSyncStatusLoading] = useState(false);
  const [auditActionLoading, setAuditActionLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);

  const refreshProductSyncStatus = useCallback(async () => {
    if (!canManageProductSync) return;

    setSyncStatusLoading(true);
    try {
      const response = await productsAPI.getSyncStatus();
      const status = response.data?.data || null;
      setSyncStatus(status);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load product snapshot status');
    } finally {
      setSyncStatusLoading(false);
    }
  }, [canManageProductSync]);

  useEffect(() => {
    if (!canManageProductSync) return;
    refreshProductSyncStatus();
  }, [canManageProductSync, refreshProductSyncStatus]);

  const handleManualAuditSnapshot = async () => {
    if (!canManageProductSync) return;

    setAuditActionLoading(true);
    try {
      const response = await productsAPI.syncAuditSnapshot();
      const message = response.data?.data?.message || 'Product audit snapshot completed.';
      toast.success(message);
      await refreshProductSyncStatus();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to trigger product audit snapshot');
    } finally {
      setAuditActionLoading(false);
    }
  };

  if (!canManageProductSync) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Product Snapshot Control</h1>
        <div className="card">
          <p className="text-gray-500">Access denied.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Product Snapshot Control</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manual audit snapshot controls for product history logging.
        </p>
      </div>

      <div className="card space-y-4">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Sync Status</h2>
            <p className="text-sm text-slate-500">
              Check audit snapshot state and trigger snapshot capture manually.
            </p>
          </div>
          <button
            type="button"
            className="btn-secondary"
            onClick={refreshProductSyncStatus}
            disabled={syncStatusLoading || auditActionLoading}
          >
            {syncStatusLoading ? 'Refreshing...' : 'Refresh Status'}
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-xs uppercase tracking-wide text-slate-500">Product Snapshot Trigger</p>
            <p className="mt-1 text-sm font-semibold text-slate-800">
              Disabled (manual audit snapshot only)
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-xs uppercase tracking-wide text-slate-500">Last Audit Snapshot</p>
            <p className="mt-1 text-sm font-semibold text-slate-800">
              {syncStatus?.auditSnapshot?.snapshotDate || 'Never'}
            </p>
            <p className="mt-1 text-xs text-slate-500">
              {syncStatus?.auditSnapshot?.attemptedAtUtc
                ? `Last checked ${new Date(syncStatus.auditSnapshot.attemptedAtUtc).toLocaleString()}`
                : syncStatus?.auditSnapshot?.loggedAtUtc
                  ? `Last captured ${new Date(syncStatus.auditSnapshot.loggedAtUtc).toLocaleString()}`
                  : 'No audit log captured yet'}
            </p>
            {formatAuditSnapshotStatus(syncStatus?.auditSnapshot) ? (
              <p className="mt-1 text-xs text-slate-500">
                {formatAuditSnapshotStatus(syncStatus.auditSnapshot)}
              </p>
            ) : null}
            {formatAuditSnapshotCounts(syncStatus?.auditSnapshot) ? (
              <p className="mt-1 text-xs text-slate-500">
                {formatAuditSnapshotCounts(syncStatus.auditSnapshot)}
              </p>
            ) : null}
            <p className="mt-1 text-[11px] text-slate-400">
              Current-day final report rows use live products. Audit snapshots mainly support day-over-day history.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[auto] lg:items-end">
          <button
            type="button"
            className="btn-primary"
            onClick={handleManualAuditSnapshot}
            disabled={syncStatusLoading || auditActionLoading}
          >
            {auditActionLoading ? 'Capturing...' : 'Manual Audit Log Sync'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductSyncControl;
